/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The kind of process-view valve.
 */
export const AnimaticValveConfigKind = {
  AngleLeft: "angle-left",
  AngleRight: "angle-right",
  BackPressureRegulator: "back-pressure-regulator",
  Block: "block",
  IcadControl: "icad-control",
  LinearGauge: "linear-gauge",
  MotorizedControl: "motorized-control",
  PneumaticControl: "pneumatic-control",
  PressureRegulator: "pressure-regulator",
  RegulatorDualSolenoidControl: "regulator-dual-solenoid-control",
  RegulatorSolenoidControl: "regulator-solenoid-control",
  Solenoid: "solenoid",
  ThreeWay: "three-way",
  TwoStepSolenoid: "two-step-solenoid",
} as const;
/**
 * The kind of process-view valve.
 */
export type AnimaticValveConfigKind = OpenEnum<typeof AnimaticValveConfigKind>;

/**
 * Optional value variant for applicable valve kinds.
 */
export const AnimaticValveConfigVariant = {
  AB: "A-B",
  BC: "B-C",
  AC: "A-C",
  Default: "default",
} as const;
/**
 * Optional value variant for applicable valve kinds.
 */
export type AnimaticValveConfigVariant = OpenEnum<
  typeof AnimaticValveConfigVariant
>;

/**
 * The configuration of the animatic for a process-view valve.
 */
export type AnimaticValveConfig = {
  /**
   * A reference to a reading or setting.
   */
  gaugeRef?: Ref | undefined;
  /**
   * The kind of process-view valve.
   */
  kind: AnimaticValveConfigKind;
  /**
   * Optional value variant for applicable valve kinds.
   */
  variant?: AnimaticValveConfigVariant | undefined;
};

/** @internal */
export const AnimaticValveConfigKind$inboundSchema: z.ZodType<
  AnimaticValveConfigKind,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(AnimaticValveConfigKind);
/** @internal */
export const AnimaticValveConfigKind$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  AnimaticValveConfigKind
> = openEnums.outboundSchema(AnimaticValveConfigKind);

/** @internal */
export const AnimaticValveConfigVariant$inboundSchema: z.ZodType<
  AnimaticValveConfigVariant,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(AnimaticValveConfigVariant);
/** @internal */
export const AnimaticValveConfigVariant$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  AnimaticValveConfigVariant
> = openEnums.outboundSchema(AnimaticValveConfigVariant);

/** @internal */
export const AnimaticValveConfig$inboundSchema: z.ZodType<
  AnimaticValveConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  gauge_ref: types.optional(Ref$inboundSchema),
  kind: AnimaticValveConfigKind$inboundSchema,
  variant: types.optional(AnimaticValveConfigVariant$inboundSchema),
}).transform((v) => {
  return remap$(v, {
    "gauge_ref": "gaugeRef",
  });
});
/** @internal */
export type AnimaticValveConfig$Outbound = {
  gauge_ref?: Ref$Outbound | undefined;
  kind: string;
  variant?: string | undefined;
};

/** @internal */
export const AnimaticValveConfig$outboundSchema: z.ZodType<
  AnimaticValveConfig$Outbound,
  z.ZodTypeDef,
  AnimaticValveConfig
> = z.object({
  gaugeRef: Ref$outboundSchema.optional(),
  kind: AnimaticValveConfigKind$outboundSchema,
  variant: AnimaticValveConfigVariant$outboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    gaugeRef: "gauge_ref",
  });
});

export function animaticValveConfigToJSON(
  animaticValveConfig: AnimaticValveConfig,
): string {
  return JSON.stringify(
    AnimaticValveConfig$outboundSchema.parse(animaticValveConfig),
  );
}
export function animaticValveConfigFromJSON(
  jsonString: string,
): SafeParseResult<AnimaticValveConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AnimaticValveConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AnimaticValveConfig' from JSON`,
  );
}
